import type {MouseEventHandler, ReactNode} from "react";
import {useMessages} from "i18n";
import {ContentSurface, Prose} from "./ContentPage";

/**
 * A failed content chunk keeps the application's existing chrome and locale because this boundary
 * remains below RootLayout. The separate root fallback supplies its own presentation dependencies
 * when the layout or its providers fail.
 */
export function ContentRouteError() {
	const {content} = useMessages();
	return <RouteErrorView heading={content.errorHeading} body={content.errorBody} retry={content.errorRetry}/>;
}

/**
 * Shared error presentation, with no app context or error-object access. Recovery stays a full document
 * load: a router navigation cannot refresh stale chunk hashes. Browser state is read only at render time.
 */
export function RouteErrorView({heading, body, retry, retryHref, onRetry, lang, children}: {
	heading: string;
	body: string;
	retry: string;
	retryHref?: string;
	onRetry?: MouseEventHandler<HTMLAnchorElement>;
	lang?: string;
	children?: ReactNode;
}) {
	return (
		<ContentSurface lang={lang}>
			<Prose>
				{children}
				<h1>{heading}</h1>
				<p>{body}</p>
				<p>
					<a href={retryHref ?? (typeof window === "undefined" ? "/" : window.location.pathname)} onClick={onRetry}>
						{retry}
					</a>
				</p>
			</Prose>
		</ContentSurface>
	);
}
